import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import MUIDataTable from "mui-datatables";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useLocation } from 'react-router-dom';
import { IconCirclePlus } from "@tabler/icons";
import { configState } from "../../../../recoil/atoms";
import CategoryIcon from '@mui/icons-material/Category';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MainCard from "../../../../styles/ui-component/cards/MainCard";
import ConfirmDialog from "../../../../utils/FormFields/ConfirmDialog";
import {Button, CircularProgress, Grid, Box, Typography} from "@mui/material";
import useAuthorization from "../../../Authentication/permissions/Authorization";
import { InventoryTemplateApi, GetCollectionFieldsResponseFields } from "../../../../api/medical-operations";

interface LocationState {
  CollectionID: string;   
  CollectionName: string; 
}

const Fields = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const config = useRecoilValue(configState);

  const [rowID, setRowID] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);  
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  
  const { canAddFields, canDeleteField } = useAuthorization();
  const [fields, setFields] = useState<GetCollectionFieldsResponseFields[]>([]);
  const collectionID = location.state ? (location.state as LocationState).CollectionID : '';
  const collectionName = location.state ? (location.state as LocationState).CollectionName : '';    

  const getFields = () => {
    setIsLoading(true);
    const field = new InventoryTemplateApi(config);
    field
      .getCollectionFields(collectionID)
      .then((res) => {
        setIsLoading(false);        
        setFields(res.data.Fields!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };  
  
  useEffect(() => {        
    getFields();      
  },[]); 

  const deleteField = (id: string) => {
    const field = new InventoryTemplateApi(config);
    field.deleteField(collectionID,id)
      .then((res) => {
        getFields();
        toast.success(res?.data?.Message!);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    setRowID(id);
    handleOpenDialog();
  };

  const handleConfirm = () => {
    deleteField(rowID);
    handleCloseDialog();
  };

  const columns = [
    {
      name: "FieldID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },             
    {
      name: "FieldName",
      label: "Field Name",
      options: {
        filter: true,
      },
    },
    {
      name: "DataType",
      label: "Type",
      options: {
        display: true,
        filter: true,
      },
    },
    {
      name: "action",
      label: "Action",
      print: false,
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          const fieldName = tableMeta.rowData[1];
          const restrictedFields = ["Name", "Quantity", "Cost", "Price"];
  
          if (restrictedFields.includes(fieldName)) {
            return null;
          }
  
          return (
            <>
              {canDeleteField && (
                <Tooltip title="Delete Field">
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{
                      ml: { xs: 0, xl: 2 },
                    }}
                    onClick={() => {
                      handleDelete(tableMeta.rowData[0]);
                    }}
                  >
                    <DeleteOutlineIcon color="error" />
                  </Button>
                </Tooltip>
              )}
            </>
          );
        },
      },
    },
  ];

  return (
    <>      
      <MainCard>
        <Grid>              
          <Box style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography variant="h3" sx={{ color: 'secondary', ml: 3 }}>
              <CategoryIcon fontSize="large" />
            </Typography>
            <Typography variant="h3" sx={{  mt: 0, ml: 2 }}>
              {collectionName}
            </Typography>
          </Box>                                        
          <MUIDataTable
            data={isLoading ? [] : fields}                              
            columns={columns}
            options={{                                    
              filter: true,
              viewColumns: false,
              selectableRows: "none",
              selectableRowsOnClick: false,
              elevation: 0,
              rowsPerPageOptions : [10,30,50,100],                    
              filterType: "dropdown",                    
              enableNestedDataAccess: ".",
              responsive: "standard",
              jumpToPage: false,
              textLabels: {
                body: {
                  noMatch: isLoading ? <CircularProgress/> : 'This Table is empty'
                }
              },                                                                                                                                                       
              customToolbar: () => {
                return (
                  canAddFields && (
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<IconCirclePlus />}                      
                      onClick={() => {
                        navigate("/add-field",
                        {
                          state: collectionID,
                        });
                      }}
                      sx={{
                        ml: "50px",
                      }}
                    >
                      Add New Field
                    </Button>
                  )
                );
              },
            }}
            title=""
          />                
          <ConfirmDialog
            name={"Field"}
            handleCloseDialog={handleCloseDialog}
            handleConfirm={handleConfirm}
            openDialog={openDialog}
          />              
        </Grid>
      </MainCard>      
    </>
  );
};

export default Fields;
